import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { setLogoutCallback } from "@/auth/logoutHandler";
import { setTokenGetter } from "@/auth/tokenHandler";
import { userService } from "@/services/userService";

/**
 * Owns the Auth0 token bridge (lets the axios interceptor fetch a live token
 * per-request via Auth0's own cache, instead of the app keeping a second,
 * unscoped copy in localStorage) and the admin-check that gates API access,
 * plus registers the logout callback the axios interceptor uses for 403s.
 */
// Auth0 error codes that silent token renewal can't resolve on its own -
// the user has to be sent through the hosted login page to grant consent,
// re-authenticate, or complete MFA.
const REQUIRES_REDIRECT = new Set(["consent_required", "login_required", "mfa_required"]);

export function useAuthSession() {
  const {
    user,
    isLoading: authLoading,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();
  const [isTokenReady, setIsTokenReady] = useState(false);

  const logoutRef = useRef(logout);
  const getTokenRef = useRef(getAccessTokenSilently);
  const loginRef = useRef(loginWithRedirect);
  useEffect(() => {
    logoutRef.current = logout;
    getTokenRef.current = getAccessTokenSilently;
    loginRef.current = loginWithRedirect;
  }, [logout, getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    setLogoutCallback((opts) =>
      logoutRef.current?.(opts ?? { logoutParams: { returnTo: window.location.origin } }),
    );
    return () => setLogoutCallback(null);
  }, []);

  useEffect(() => {
    if (!user) return;

    setTokenGetter(() => getTokenRef.current());
    let cancelled = false;

    (async () => {
      try {
        await getTokenRef.current();
        if (cancelled) return;
        setIsTokenReady(true);
      } catch (e) {
        const code = e && typeof e === "object" && "error" in e ? (e as { error: string }).error : undefined;
        if (code && REQUIRES_REDIRECT.has(code)) {
          loginRef.current({ appState: { returnTo: window.location.pathname } });
          return;
        }
        console.log(e instanceof Error ? e.message : e);
        return;
      }
      // Non-admins get 403 and are logged out by the API interceptor.
      userService.getMe().catch(() => {});
    })();

    return () => {
      cancelled = true;
      setTokenGetter(null);
    };
  }, [user]);

  return { authLoading, isTokenReady, user };
}
